/* PDP media gallery.
   One scroll-snap track serves both breakpoints: mobile swipes with dots,
   desktop shows the same track one slide tall with vertical thumbnails.
   Clicking an image opens the lightbox (dotto-modal) with a 2400px source.
   No library, no cloning — just scroll-snap + IntersectionObserver. */

class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));
    this.dots = Array.from(this.querySelectorAll('.pdp__dot'));
    this.lightbox = this.querySelector('dotto-modal');
    this.lightboxImg = this.querySelector('[data-lightbox-img]');
    this.lightboxPanel = this.querySelector('.pdp__lightbox-panel');
    this.zoomTriggers = Array.from(this.querySelectorAll('[data-zoom-src]'));
    this.lightboxPrevious = this.querySelector('[data-lightbox-previous]');
    this.lightboxNext = this.querySelector('[data-lightbox-next]');
    this.lightboxCounter = this.querySelector('[data-lightbox-counter]');
    this.lightboxZoom = this.querySelector('[data-lightbox-zoom]');
    this.lightboxIndex = 0;
    this.panStart = null;
    this.panMoved = false;
    this.lightboxSwipeStart = null;
    if (!this.track || this.slides.length === 0) return;

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(parseInt(thumb.dataset.thumb, 10)));
    });

    if (this.lightboxImg && this.lightboxPanel) {
      this.lightboxImg.addEventListener('click', (event) => {
        if (this.panMoved) {
          event.preventDefault();
          this.panMoved = false;
          return;
        }
        this.toggleZoom();
      });
      this.lightboxImg.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.toggleZoom();
        }
      });

      this.lightboxPanel.addEventListener('pointerdown', (event) => {
        if (!window.matchMedia('(min-width: 750px)').matches) {
          if (!event.target.closest('button, a')) {
            this.lightboxSwipeStart = { pointerId: event.pointerId, x: event.clientX, y: event.clientY };
          }
          return;
        }
        if (!this.lightboxPanel.classList.contains('is-zoomed') || event.button !== 0) return;
        this.panStart = {
          pointerId: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          left: this.lightboxPanel.scrollLeft,
          top: this.lightboxPanel.scrollTop
        };
        this.panMoved = false;
        this.lightboxPanel.classList.add('is-panning');
        this.lightboxPanel.setPointerCapture(event.pointerId);
      });
      this.lightboxPanel.addEventListener('pointermove', (event) => {
        if (!this.panStart || event.pointerId !== this.panStart.pointerId) return;
        const dx = event.clientX - this.panStart.x;
        const dy = event.clientY - this.panStart.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) this.panMoved = true;
        this.lightboxPanel.scrollLeft = this.panStart.left - dx;
        this.lightboxPanel.scrollTop = this.panStart.top - dy;
      });
      const endPan = (event) => {
        if (!this.panStart || event.pointerId !== this.panStart.pointerId) return;
        this.lightboxPanel.classList.remove('is-panning');
        this.panStart = null;
      };
      this.lightboxPanel.addEventListener('pointerup', endPan);
      this.lightboxPanel.addEventListener('pointercancel', endPan);
      this.lightboxPanel.addEventListener('pointerup', (event) => this.completeLightboxSwipe(event));
      this.lightboxPanel.addEventListener('pointercancel', () => { this.lightboxSwipeStart = null; });
    }

    let pointerStart = null;
    this.addEventListener('pointerdown', (event) => {
      pointerStart = { x: event.clientX, y: event.clientY };
    });
    this.zoomTriggers.forEach((trigger, index) => {
      trigger.addEventListener('click', (event) => {
        if (!this.lightbox || !this.lightboxImg) return;
        if (pointerStart) {
          const moved = Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y);
          if (moved > 10) return;
        }
        this.setLightboxImage(index);
        this.lightbox.open(trigger);
      });
    });

    this.lightboxPrevious?.addEventListener('click', () => this.stepLightbox(-1));
    this.lightboxNext?.addEventListener('click', () => this.stepLightbox(1));
    this.lightboxZoom?.addEventListener('click', () => this.toggleZoom());
    this.lightbox?.addEventListener('keydown', (event) => {
      if (!this.lightbox.isOpen) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.stepLightbox(-1);
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.stepLightbox(1);
      }
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => observer.observe(slide));

    this.track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') this.goTo(Math.min(this.current + 1, this.slides.length - 1));
      if (event.key === 'ArrowLeft') this.goTo(Math.max(this.current - 1, 0));
    });

    this.current = 0;
  }

  centerZoom() {
    if (!this.lightboxPanel) return;
    const left = (this.lightboxPanel.scrollWidth - this.lightboxPanel.clientWidth) / 2;
    const top = (this.lightboxPanel.scrollHeight - this.lightboxPanel.clientHeight) / 2;
    this.lightboxPanel.scrollTo({ left, top });
  }

  completeLightboxSwipe(event) {
    if (!this.lightboxSwipeStart || event.pointerId !== this.lightboxSwipeStart.pointerId) return;
    const dx = event.clientX - this.lightboxSwipeStart.x;
    const dy = event.clientY - this.lightboxSwipeStart.y;
    this.lightboxSwipeStart = null;
    if (Math.abs(dx) < 42 || Math.abs(dx) <= Math.abs(dy)) return;
    this.stepLightbox(dx < 0 ? 1 : -1);
  }

  toggleZoom() {
    if (!this.lightboxPanel || !this.lightboxImg || !window.matchMedia('(min-width: 750px)').matches) return;
    const zoomed = this.lightboxPanel.classList.toggle('is-zoomed');
    this.lightboxImg.setAttribute('aria-pressed', String(zoomed));
    if (this.lightboxZoom) {
      this.lightboxZoom.setAttribute('aria-pressed', String(zoomed));
      this.lightboxZoom.textContent = zoomed ? 'Zoom out' : 'Zoom in';
    }
    if (zoomed) {
      requestAnimationFrame(() => this.centerZoom());
    } else {
      this.lightboxPanel.scrollTo({ top: 0, left: 0 });
    }
  }

  setLightboxImage(index) {
    const trigger = this.zoomTriggers[index];
    if (!trigger || !this.lightboxImg) return;

    // Paint from the existing slide first, then upgrade via the supplied srcset.
    const slideImg = trigger.querySelector('img');
    if (slideImg && slideImg.currentSrc) this.lightboxImg.src = slideImg.currentSrc;
    const w = parseInt(trigger.dataset.zoomWidth, 10);
    const h = parseInt(trigger.dataset.zoomHeight, 10);
    if (w && h) {
      this.lightboxImg.width = w;
      this.lightboxImg.height = h;
    }
    this.lightboxImg.sizes = '(min-width: 750px) 60rem, 100vw';
    this.lightboxImg.srcset = trigger.dataset.zoomSrcset || '';
    if (!slideImg || !slideImg.currentSrc) this.lightboxImg.src = trigger.dataset.zoomSrc;
    if (slideImg?.alt) this.lightboxImg.alt = slideImg.alt;

    this.lightboxIndex = index;
    if (this.lightboxPanel) {
      this.lightboxPanel.classList.remove('is-zoomed', 'is-panning');
      this.lightboxPanel.scrollTo({ top: 0, left: 0 });
      this.lightboxImg.setAttribute('aria-pressed', 'false');
    }
    if (this.lightboxZoom) {
      this.lightboxZoom.setAttribute('aria-pressed', 'false');
      this.lightboxZoom.textContent = 'Zoom in';
    }
    this.updateLightboxControls();
  }

  stepLightbox(direction) {
    const nextIndex = this.lightboxIndex + direction;
    if (nextIndex < 0 || nextIndex >= this.zoomTriggers.length) return;
    this.setLightboxImage(nextIndex);
  }

  updateLightboxControls() {
    const total = this.zoomTriggers.length;
    if (this.lightboxPrevious) this.lightboxPrevious.disabled = this.lightboxIndex === 0;
    if (this.lightboxNext) this.lightboxNext.disabled = this.lightboxIndex === total - 1;
    if (this.lightboxCounter) this.lightboxCounter.textContent = `${this.lightboxIndex + 1} / ${total}`;
  }

  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    this.setActive(index);
  }

  setActive(index) {
    if (index < 0 || index === this.current) return;
    this.current = index;
    this.thumbs.forEach((thumb, i) => {
      thumb.classList.toggle('is-active', i === index);
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
  }
}

if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);
